import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Skeleton } from '../components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { JobCreationPanel } from '../components/JobCreationPanel';
import {
  Plus, Search, Briefcase, Users, Trash2, ChevronRight, Calendar,
  Loader2, FileText, Merge, Pencil, Building2, ArrowUpDown, X, Globe2,
  RotateCcw, AlertTriangle, Link2
} from 'lucide-react';
import { toast } from 'sonner';
import { createJob, getJobs, deleteJob, analyzeJD, updateJob, mergeJobs, moveJobRegion, bulkMoveJobsRegion, getDeletedJobs, restoreJob, permanentDeleteJob } from '../services/api';
import { useRegion, getRegionLabel } from '../context/RegionContext';
import { format, formatDistanceToNow, parseISO } from 'date-fns';
import { PublicLinkModal } from '../components/PublicLinkModal';
import ATSJobImportModal from '../components/ATSJobImportModal';
import { JobsPager } from '../components/JobsPager';

const PAGE_SIZE = 25;

const REGIONS = [
  { key: 'NA', label: 'North America' },
  { key: 'UK_EMEA', label: 'UK & EMEA' },
  { key: 'APAC', label: 'APAC' },
];

export default function JobsPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { region, regionLabel } = useRegion();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(searchParams.get('new') === '1');
  const [showATSImport, setShowATSImport] = useState(false);
  const [showNewJobMenu, setShowNewJobMenu] = useState(false);
  const newJobMenuRef = React.useRef(null);

  React.useEffect(() => {
    const handler = (e) => { if (newJobMenuRef.current && !newJobMenuRef.current.contains(e.target)) setShowNewJobMenu(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const [creatingJob, setCreatingJob] = useState(false);
  const [analyzingJD, setAnalyzingJD] = useState(false);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [jobToDelete, setJobToDelete] = useState(null);

  const [searchQuery, setSearchQuery] = useState('');
  const [clientFilter, setClientFilter] = useState('all');
  const [sortBy, setSortBy] = useState('date_desc');
  const [page, setPage] = useState(1);
  useEffect(() => { setPage(1); }, [searchQuery, clientFilter, sortBy, region]);

  // Merge state
  const [selectedForMerge, setSelectedForMerge] = useState([]);
  const [mergeDialogOpen, setMergeDialogOpen] = useState(false);
  const [mergeTarget, setMergeTarget] = useState('');
  const [merging, setMerging] = useState(false);

  // Edit state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingJob, setEditingJob] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);

  // Move region state
  const [moveDialogOpen, setMoveDialogOpen] = useState(false);
  const [moveTarget, setMoveTarget] = useState('');
  const [movingRegion, setMovingRegion] = useState(false);
  const [moveMode, setMoveMode] = useState('single');
  const [moveJobId, setMoveJobId] = useState(null);

  // Deleted/Recycle bin
  const [activeTab, setActiveTab] = useState('active'); // 'active' | 'deleted'
  const [deletedJobs, setDeletedJobs] = useState([]);
  const [loadingDeleted, setLoadingDeleted] = useState(false);
  const [permanentDeleteDialogOpen, setPermanentDeleteDialogOpen] = useState(false);
  const [jobToPermanentDelete, setJobToPermanentDelete] = useState(null);

  // Public link modal
  const [publicLinkModalOpen, setPublicLinkModalOpen] = useState(false);
  const [publicLinkJobId, setPublicLinkJobId] = useState(null);
  const [publicLinkJobTitle, setPublicLinkJobTitle] = useState('');
  const [publicLinkHasQuestions, setPublicLinkHasQuestions] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchJobs(); }, [region]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (activeTab === 'deleted') fetchDeletedJobs(); }, [activeTab, region]);

  const fetchDeletedJobs = async () => {
    try {
      setLoadingDeleted(true);
      const data = await getDeletedJobs(region);
      setDeletedJobs(data);
    } catch (e) { console.error(e); }
    finally { setLoadingDeleted(false); }
  };

  const handleRestore = async (jobId) => {
    try {
      const result = await restoreJob(jobId);
      toast.success(`Restored "${result.title}"`);
      fetchDeletedJobs();
      fetchJobs();
    } catch (e) { toast.error('Failed to restore'); }
  };

  const handlePermanentDelete = async () => {
    if (!jobToPermanentDelete) return;
    try {
      await permanentDeleteJob(jobToPermanentDelete);
      toast.success('Job permanently deleted');
      setPermanentDeleteDialogOpen(false);
      setJobToPermanentDelete(null);
      fetchDeletedJobs();
    } catch (e) { toast.error('Failed to permanently delete'); }
  };

  const fetchJobs = async () => {
    try {
      setLoading(true);
      const data = await getJobs(region);
      setJobs(data);
    } catch (e) {
      toast.error('Failed to load jobs');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateJob = async (formData) => {
    try {
      setCreatingJob(true);
      const job = await createJob({ ...formData, region });
      toast.success('Job created successfully');
      setAnalyzingJD(true);
      await analyzeJD(job.id);
      toast.success('JD analysis complete!');
      fetchJobs();
      setShowCreate(false);
      navigate(`/jobs/${job.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create job');
    } finally {
      setCreatingJob(false);
      setAnalyzingJD(false);
    }
  };

  const handleDeleteJob = async () => {
    if (!jobToDelete) return;
    try {
      await deleteJob(jobToDelete);
      toast.success('Job deleted');
      setSelectedForMerge(prev => prev.filter(id => id !== jobToDelete));
      fetchJobs();
    } catch (e) {
      toast.error('Failed to delete job');
    } finally {
      setDeleteDialogOpen(false);
      setJobToDelete(null);
    }
  };

  const handleEditJob = (job, e) => {
    e.stopPropagation();
    setEditingJob(job);
    setEditForm({
      title: job.title || '',
      job_type: job.job_type || '',
      city: job.city || '',
      country: job.country || '',
      client_name: job.client_name || ''
    });
    setEditDialogOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!editingJob) return;
    try {
      setSaving(true);
      await updateJob(editingJob.id, editForm);
      toast.success('Job updated');
      fetchJobs();
      setEditDialogOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  const toggleMergeSelect = (jobId, e) => {
    e.stopPropagation();
    setSelectedForMerge(prev =>
      prev.includes(jobId) ? prev.filter(id => id !== jobId) : [...prev, jobId]
    );
  };

  const openMergeDialog = () => {
    if (selectedForMerge.length < 2) {
      toast.error('Select at least 2 jobs to merge');
      return;
    }
    setMergeTarget(selectedForMerge[0]);
    setMergeDialogOpen(true);
  };

  const handleMerge = async () => {
    if (!mergeTarget) { toast.error('Select a target job'); return; }
    try {
      setMerging(true);
      const sourceIds = selectedForMerge.filter(id => id !== mergeTarget);
      const result = await mergeJobs(mergeTarget, sourceIds);
      toast.success(`Merged! ${result.merged_candidates} candidates and ${result.merged_interviews} interviews combined.`);
      setSelectedForMerge([]);
      setMergeDialogOpen(false);
      fetchJobs();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to merge');
    } finally {
      setMerging(false);
    }
  };

  const handleMoveRegion = async () => {
    if (!moveTarget) { toast.error('Select a target region'); return; }
    try {
      setMovingRegion(true);
      if (moveMode === 'bulk' && selectedForMerge.length > 0) {
        const result = await bulkMoveJobsRegion(selectedForMerge, moveTarget);
        toast.success(`Moved ${result.moved_jobs} jobs (${result.total_candidates} candidates, ${result.total_interviews} interviews) to ${getRegionLabel(moveTarget)}`);
        setSelectedForMerge([]);
      } else if (moveJobId) {
        const result = await moveJobRegion(moveJobId, moveTarget);
        toast.success(`Moved "${result.job_title}" to ${getRegionLabel(moveTarget)} (${result.moved_candidates} candidates, ${result.moved_interviews} interviews)`);
      }
      setMoveDialogOpen(false);
      setMoveTarget('');
      setMoveJobId(null);
      fetchJobs();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to move');
    } finally {
      setMovingRegion(false);
    }
  };

  const openMoveDialog = (jobId = null, mode = 'single') => {
    setMoveMode(mode);
    setMoveJobId(jobId);
    setMoveTarget('');
    setMoveDialogOpen(true);
  };

  // Derived data
  const clientNames = [...new Set(jobs.map(j => j.client_name).filter(Boolean))];

  const filteredJobs = jobs
    .filter(j => {
      const q = searchQuery.toLowerCase();
      const matchSearch = !searchQuery ||
        j.title?.toLowerCase().includes(q) ||
        (j.client_name || '').toLowerCase().includes(q) ||
        (j.ats_job_code || '').toLowerCase().includes(q);
      const matchClient = clientFilter === 'all' || j.client_name === clientFilter;
      return matchSearch && matchClient;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'date_asc': return new Date(a.created_at) - new Date(b.created_at);
        case 'title_asc': return (a.title || '').localeCompare(b.title || '');
        case 'title_desc': return (b.title || '').localeCompare(a.title || '');
        case 'candidates_desc': return (b.total_resumes || 0) - (a.total_resumes || 0);
        case 'client_asc': return (a.client_name || '').localeCompare(b.client_name || '');
        default: return new Date(b.created_at) - new Date(a.created_at);
      }
    });

  const selectedMergeJobs = jobs.filter(j => selectedForMerge.includes(j.id));
  const pagedJobs = filteredJobs.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <div className="space-y-6" data-testid="jobs-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Jobs
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {regionLabel} &middot; Manage your job postings and analyses
          </p>
        </div>
        <div className="relative" ref={newJobMenuRef}>
          <Button onClick={() => setShowNewJobMenu(!showNewJobMenu)} data-testid="create-job-toggle-btn">
            <Plus className="h-4 w-4 mr-2" />
            New Job
          </Button>
          {showNewJobMenu && (
            <div className="absolute right-0 top-full mt-1 w-52 bg-background border rounded-xl shadow-xl z-50 py-1.5 animate-in fade-in-0 zoom-in-95" data-testid="new-job-menu">
              <button className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-muted/50 transition-colors text-left"
                onClick={() => { setShowNewJobMenu(false); setShowATSImport(true); }}
                data-testid="new-job-from-ats">
                <Globe2 className="h-4 w-4 text-orange-500" />
                <div>
                  <p className="text-sm font-medium">Add from ATS</p>
                  <p className="text-[10px] text-muted-foreground">Import from Ceipal</p>
                </div>
              </button>
              <button className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-muted/50 transition-colors text-left"
                onClick={() => { setShowNewJobMenu(false); setShowCreate(!showCreate); }}
                data-testid="new-job-manual">
                <Pencil className="h-4 w-4 text-indigo-500" />
                <div>
                  <p className="text-sm font-medium">Add Manually</p>
                  <p className="text-[10px] text-muted-foreground">Create from scratch</p>
                </div>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Create Job Panel */}
      {showCreate && (
        <Card className="border-indigo-500/20" data-testid="job-creation-section">
          <CardContent className="p-6">
            <JobCreationPanel onJobCreated={handleCreateJob} isLoading={creatingJob || analyzingJD} />
            {analyzingJD && (
              <div className="mt-4 text-center">
                <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2 text-indigo-500" />
                <p className="text-sm text-muted-foreground">AI is analyzing the job description...</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Active / Deleted Tabs */}
      <div className="flex items-center gap-1 border-b" data-testid="jobs-tabs">
        <button
          onClick={() => setActiveTab('active')}
          className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'active'
              ? 'border-primary text-primary'
              : 'border-transparent text-muted-foreground hover:text-foreground'
          }`}
          data-testid="jobs-tab-active"
        >
          <Briefcase className="h-3.5 w-3.5 inline mr-1.5" />
          Active Jobs
        </button>
        <button
          onClick={() => setActiveTab('deleted')}
          className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors flex items-center gap-1.5 ${
            activeTab === 'deleted'
              ? 'border-red-500 text-red-500'
              : 'border-transparent text-muted-foreground hover:text-foreground'
          }`}
          data-testid="jobs-tab-deleted"
        >
          <Trash2 className="h-3.5 w-3.5" />
          Recycle Bin
          {deletedJobs.length > 0 && (
            <Badge variant="destructive" className="text-[10px] px-1.5 py-0 h-4">{deletedJobs.length}</Badge>
          )}
        </button>
      </div>

      {/* DELETED JOBS VIEW */}
      {activeTab === 'deleted' ? (
        <div className="space-y-4" data-testid="deleted-jobs-view">
          {loadingDeleted ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : deletedJobs.length === 0 ? (
            <div className="text-center py-16">
              <Trash2 className="h-10 w-10 mx-auto mb-3 text-muted-foreground/30" />
              <p className="text-sm text-muted-foreground">Recycle bin is empty</p>
              <p className="text-xs text-muted-foreground/60 mt-1">Deleted jobs will appear here for recovery</p>
            </div>
          ) : (
            <div className="space-y-3">
              {deletedJobs.map((job) => {
                let deletedAgo = '';
                try { deletedAgo = formatDistanceToNow(parseISO(job.deleted_at), { addSuffix: true }); } catch { deletedAgo = ''; }
                return (
                  <div
                    key={job.id}
                    className="flex items-center gap-4 p-4 rounded-lg border border-dashed border-red-500/20 bg-red-500/5"
                    data-testid={`deleted-job-${job.id}`}
                  >
                    <div className="h-10 w-10 rounded-lg bg-red-500/10 flex items-center justify-center shrink-0">
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{job.title}</p>
                      <div className="flex items-center gap-3 text-xs text-muted-foreground mt-0.5">
                        {job.client_name && <span>{job.client_name}</span>}
                        <span>{job.total_resumes || 0} candidates</span>
                        {deletedAgo && <span className="text-red-400">Deleted {deletedAgo}</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Button
                        size="sm" variant="outline"
                        className="gap-1.5 text-emerald-600 border-emerald-500/30 hover:bg-emerald-500/10"
                        onClick={() => handleRestore(job.id)}
                        data-testid={`restore-job-${job.id}`}
                      >
                        <RotateCcw className="h-3.5 w-3.5" /> Restore
                      </Button>
                      <Button
                        size="sm" variant="outline"
                        className="gap-1.5 text-red-500 border-red-500/30 hover:bg-red-500/10"
                        onClick={() => { setJobToPermanentDelete(job.id); setPermanentDeleteDialogOpen(true); }}
                        data-testid={`perm-delete-job-${job.id}`}
                      >
                        <AlertTriangle className="h-3.5 w-3.5" /> Delete Forever
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      ) : (
      <>

      {/* Merge & Move Banner */}
      {selectedForMerge.length > 0 && (
        <div className="flex items-center justify-between p-3 bg-indigo-500/10 border border-indigo-500/20 rounded-lg" data-testid="merge-banner">
          <div className="flex items-center gap-2 text-sm">
            <Merge className="h-4 w-4 text-indigo-500" />
            <span><strong>{selectedForMerge.length}</strong> jobs selected</span>
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={() => openMoveDialog(null, 'bulk')} data-testid="bulk-move-region-btn">
              <Globe2 className="h-3.5 w-3.5 mr-1" /> Move Region
            </Button>
            {selectedForMerge.length >= 2 && (
              <Button size="sm" onClick={openMergeDialog} data-testid="merge-jobs-btn">
                <Merge className="h-3.5 w-3.5 mr-1" /> Merge Jobs
              </Button>
            )}
            <Button variant="ghost" size="sm" onClick={() => setSelectedForMerge([])} data-testid="clear-merge-btn">
              <X className="h-3.5 w-3.5 mr-1" /> Clear
            </Button>
          </div>
        </div>
      )}

      {/* Search & Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by title, client or job code..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
            data-testid="jobs-search-input"
          />
        </div>
        {clientNames.length > 0 && (
          <Select value={clientFilter} onValueChange={setClientFilter}>
            <SelectTrigger className="w-[180px]" data-testid="jobs-client-filter">
              <Building2 className="h-3.5 w-3.5 mr-1.5 text-muted-foreground" />
              <SelectValue placeholder="All Clients" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Clients</SelectItem>
              {clientNames.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        <Select value={sortBy} onValueChange={setSortBy}>
          <SelectTrigger className="w-[180px]" data-testid="jobs-sort-select">
            <ArrowUpDown className="h-3.5 w-3.5 mr-1.5 text-muted-foreground" />
            <SelectValue placeholder="Sort by" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="date_desc">Newest First</SelectItem>
            <SelectItem value="date_asc">Oldest First</SelectItem>
            <SelectItem value="title_asc">Title A-Z</SelectItem>
            <SelectItem value="title_desc">Title Z-A</SelectItem>
            <SelectItem value="candidates_desc">Most Candidates</SelectItem>
            <SelectItem value="client_asc">Client A-Z</SelectItem>
          </SelectContent>
        </Select>
        <Badge variant="outline" className="text-xs px-3 py-1.5">{filteredJobs.length.toLocaleString()} {filteredJobs.length === 1 ? 'job' : 'jobs'}</Badge>
      </div>

      {/* Jobs List */}
      {loading ? (
        <div className="space-y-3">{[1,2,3].map(i => <Skeleton key={i} className="h-24" />)}</div>
      ) : filteredJobs.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center">
            <Briefcase className="h-12 w-12 mx-auto mb-4 text-muted-foreground/30" />
            <p className="text-lg font-medium">No jobs found</p>
            <p className="text-sm text-muted-foreground mt-1">
              {searchQuery || clientFilter !== 'all' ? 'Try different filters' : 'Create your first job to get started'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {pagedJobs.map((job) => {
            const createdDate = job.created_at ? format(new Date(job.created_at), 'MMM d, yyyy') : '';
            const isSelected = selectedForMerge.includes(job.id);
            return (
              <Card
                key={job.id}
                className={`group transition-all cursor-pointer ${
                  isSelected ? 'border-indigo-500 bg-indigo-500/5' : 'hover:border-indigo-500/30'
                }`}
                onClick={() => navigate(`/jobs/${job.id}`)}
                data-testid={`job-card-${job.id}`}
              >
                <CardContent className="p-5">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4 min-w-0">
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={() => {}}
                        onClick={(e) => toggleMergeSelect(job.id, e)}
                        className="shrink-0"
                        data-testid={`merge-checkbox-${job.id}`}
                      />
                      <div className="h-11 w-11 rounded-xl bg-indigo-500/10 flex items-center justify-center shrink-0">
                        <FileText className="h-5 w-5 text-indigo-500" strokeWidth={1.5} />
                      </div>
                      <div className="min-w-0">
                        <h3 className="font-semibold text-base truncate" style={{ fontFamily: 'Manrope, sans-serif' }}>
                          {job.title}
                        </h3>
                        <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                          <Badge variant="outline" className="text-xs">{job.job_type || 'N/A'}</Badge>
                          {job.client_name && (
                            <Badge variant="secondary" className="text-xs gap-1">
                              <Building2 className="h-3 w-3" /> {job.client_name}
                            </Badge>
                          )}
                          {job.ats_job_code && (
                            <Badge variant="outline" className="text-[10px] font-mono" data-testid={`job-ats-code-${job.id}`}>
                              {job.ats_job_code}{job.ats_job_status ? ` · ${job.ats_job_status}` : ''}
                            </Badge>
                          )}
                          {job.ats_auto_imported && (
                            <Badge className="text-[10px] bg-indigo-500/10 text-indigo-600 dark:text-indigo-300 border-indigo-500/20 hover:bg-indigo-500/10" data-testid={`job-auto-imported-${job.id}`}>
                              Auto-imported
                            </Badge>
                          )}
                          {job.city && (
                            <span className="text-xs text-muted-foreground">{job.city}, {job.country}</span>
                          )}
                          {createdDate && (
                            <span className="text-xs text-muted-foreground flex items-center gap-1">
                              <Calendar className="h-3 w-3" /> {createdDate}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 shrink-0">
                      <div className="flex items-center gap-4 text-sm">
                        <div className="text-center">
                          <p className="font-bold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{job.total_resumes || 0}</p>
                          <p className="text-[10px] text-muted-foreground">Resumes</p>
                        </div>
                        {job.top_score && (
                          <div className="text-center">
                            <p className="font-bold text-emerald-500" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{Math.round(job.top_score)}</p>
                            <p className="text-[10px] text-muted-foreground">Top</p>
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <Button
                          variant="ghost" size="icon" className="h-8 w-8 text-indigo-500 hover:text-indigo-600 hover:bg-indigo-500/10"
                          onClick={(e) => {
                            e.stopPropagation();
                            setPublicLinkJobId(job.id);
                            setPublicLinkJobTitle(job.title);
                            setPublicLinkHasQuestions(!!job.has_questions);
                            setPublicLinkModalOpen(true);
                          }}
                          title="Public Test Link"
                          data-testid={`public-link-${job.id}`}
                        >
                          <Link2 className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          variant="ghost" size="icon" className="h-8 w-8"
                          onClick={(e) => { e.stopPropagation(); openMoveDialog(job.id, 'single'); }}
                          title="Move to another region"
                          data-testid={`move-region-${job.id}`}
                        >
                          <Globe2 className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          variant="ghost" size="icon" className="h-8 w-8"
                          onClick={(e) => handleEditJob(job, e)}
                          data-testid={`edit-job-${job.id}`}
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          variant="ghost" size="icon"
                          className="h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10"
                          onClick={(e) => { e.stopPropagation(); setJobToDelete(job.id); setDeleteDialogOpen(true); }}
                          data-testid={`delete-job-${job.id}`}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                      <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:text-foreground" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
          <JobsPager page={page} pageSize={PAGE_SIZE} total={filteredJobs.length} onPage={(p) => { setPage(p); window.scrollTo({ top: 0, behavior: 'smooth' }); }} />
        </div>
      )}

      </>
      )}

      {/* Delete Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Job?</AlertDialogTitle>
            <AlertDialogDescription>
              This will move the job and all associated data to the recycle bin. You can restore it later.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteJob}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="confirm-delete-btn"
            >Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="sm:max-w-[480px]" data-testid="edit-job-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Edit Job</DialogTitle>
            <DialogDescription>Update job details below.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="edit-title">Job Title</Label>
              <Input
                id="edit-title"
                value={editForm.title}
                onChange={(e) => setEditForm(p => ({ ...p, title: e.target.value }))}
                data-testid="edit-job-title-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-type">Job Type</Label>
                <Select value={editForm.job_type} onValueChange={(v) => setEditForm(p => ({ ...p, job_type: v }))}>
                  <SelectTrigger data-testid="edit-job-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="remote">Remote</SelectItem>
                    <SelectItem value="onsite">Onsite</SelectItem>
                    <SelectItem value="hybrid">Hybrid</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-client">Client Name</Label>
                <Input
                  id="edit-client"
                  value={editForm.client_name}
                  onChange={(e) => setEditForm(p => ({ ...p, client_name: e.target.value }))}
                  placeholder="e.g., Acme Corp"
                  data-testid="edit-client-name-input"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-city">City</Label>
                <Input
                  id="edit-city"
                  value={editForm.city}
                  onChange={(e) => setEditForm(p => ({ ...p, city: e.target.value }))}
                  data-testid="edit-city-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-country">Country</Label>
                <Input
                  id="edit-country"
                  value={editForm.country}
                  onChange={(e) => setEditForm(p => ({ ...p, country: e.target.value }))}
                  data-testid="edit-country-input"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveEdit} disabled={saving} data-testid="save-edit-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Merge Dialog */}
      <Dialog open={mergeDialogOpen} onOpenChange={setMergeDialogOpen}>
        <DialogContent className="sm:max-w-[520px]" data-testid="merge-jobs-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <Merge className="h-5 w-5 text-indigo-500" /> Merge Jobs
            </DialogTitle>
            <DialogDescription>
              All candidates and interviews from source jobs will be merged into the target job. Source jobs will be deleted.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Target Job (keep this one)</Label>
              <Select value={mergeTarget} onValueChange={setMergeTarget}>
                <SelectTrigger data-testid="merge-target-select">
                  <SelectValue placeholder="Select target job" />
                </SelectTrigger>
                <SelectContent>
                  {selectedMergeJobs.map(j => (
                    <SelectItem key={j.id} value={j.id}>{j.title}{j.client_name ? ` (${j.client_name})` : ''}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Jobs to merge (will be deleted)</Label>
              <div className="space-y-1.5 max-h-[200px] overflow-y-auto">
                {selectedMergeJobs.filter(j => j.id !== mergeTarget).map(j => (
                  <div key={j.id} className="flex items-center justify-between p-2.5 bg-destructive/5 border border-destructive/20 rounded-lg text-sm">
                    <div className="min-w-0">
                      <p className="font-medium truncate">{j.title}</p>
                      <p className="text-xs text-muted-foreground">{j.total_resumes || 0} candidates{j.client_name ? ` | ${j.client_name}` : ''}</p>
                    </div>
                    <Badge variant="destructive" className="text-[10px] shrink-0">Will be merged & deleted</Badge>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMergeDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleMerge} disabled={merging} data-testid="confirm-merge-btn">
              {merging ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Merge className="h-4 w-4 mr-2" />}
              Merge {selectedForMerge.length} Jobs
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Move Region Dialog */}
      <Dialog open={moveDialogOpen} onOpenChange={setMoveDialogOpen}>
        <DialogContent data-testid="move-region-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Globe2 className="h-5 w-5 text-indigo-500" />
              Move {moveMode === 'bulk' ? `${selectedForMerge.length} Jobs` : 'Job'} to Region
            </DialogTitle>
            <DialogDescription>
              The job(s) along with all candidates, interview sessions, and notifications will be transferred to the selected region.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {moveMode === 'bulk' && (
              <div className="space-y-1.5 max-h-[150px] overflow-y-auto">
                {jobs.filter(j => selectedForMerge.includes(j.id)).map(j => (
                  <div key={j.id} className="flex items-center gap-2 p-2 bg-muted/50 rounded-lg text-sm">
                    <FileText className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                    <span className="truncate">{j.title}</span>
                    <span className="text-xs text-muted-foreground ml-auto shrink-0">{j.total_resumes || 0} candidates</span>
                  </div>
                ))}
              </div>
            )}
            <div className="space-y-2">
              <Label>Target Region</Label>
              <div className="grid grid-cols-3 gap-3">
                {REGIONS.filter(r => r.key !== region).map(r => (
                  <button
                    key={r.key}
                    onClick={() => setMoveTarget(r.key)}
                    className={`p-3 rounded-lg border text-center transition-all ${
                      moveTarget === r.key
                        ? 'border-indigo-500 bg-indigo-500/10 ring-1 ring-indigo-500/30'
                        : 'border-border hover:border-muted-foreground/30'
                    }`}
                    data-testid={`move-target-${r.key}`}
                  >
                    <p className="text-sm font-medium">{r.label}</p>
                  </button>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMoveDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleMoveRegion} disabled={movingRegion || !moveTarget} data-testid="confirm-move-region-btn">
              {movingRegion ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Globe2 className="h-4 w-4 mr-2" />}
              Move to {moveTarget ? getRegionLabel(moveTarget) : 'Region'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Permanent Delete Dialog */}
      <AlertDialog open={permanentDeleteDialogOpen} onOpenChange={setPermanentDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-500">
              <AlertTriangle className="h-5 w-5" /> Permanently Delete?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove the job, all candidates, interview sessions, and related data. This action <strong>cannot be undone</strong>.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              onClick={handlePermanentDelete}
              data-testid="confirm-permanent-delete-btn"
            >
              Delete Forever
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Public Link Modal */}
      <PublicLinkModal
        isOpen={publicLinkModalOpen}
        onClose={() => { setPublicLinkModalOpen(false); setPublicLinkJobId(null); }}
        jobId={publicLinkJobId}
        jobTitle={publicLinkJobTitle}
        hasQuestions={publicLinkHasQuestions}
      />
      <ATSJobImportModal
        isOpen={showATSImport}
        onClose={() => setShowATSImport(false)}
        region={region}
        onImported={fetchJobs}
      />
    </div>
  );
}
